import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaEnvelope, FaUserCheck, FaSignInAlt } from 'react-icons/fa';
import axios from 'axios';
import { getFirstName } from '../utils/nameUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname.includes('localhost') ? 'http://localhost:5000/api' : '/api');

export const StudentLoginModal = ({ isOpen, onClose }) => {
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const savedEmail = localStorage.getItem('datx_user_email') || '';
      setEmailInput(savedEmail);
      setMessage('');
      setIsSuccess(false);

      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!emailInput || !emailInput.includes('@')) {
      setMessage('يرجى كتابة البريد الإلكتروني بشكل صحيح');
      setIsSuccess(false);
      return;
    }

    setLoading(true);
    setMessage('');

    const targetEmail = emailInput.trim().toLowerCase();

    try {
      const res = await axios.get(`${API_BASE_URL}/registrations/lookup-student`, {
        params: { email: targetEmail },
      });

      if (res.data?.found && res.data?.student) {
        const student = res.data.student;
        localStorage.setItem('datx_user_email', student.email);
        localStorage.setItem('datx_user_name', student.name);
        if (res.data?.token) {
          localStorage.setItem('accessToken', res.data.token);
          localStorage.setItem('datx_token', res.data.token);
          localStorage.setItem('datx_student_token', res.data.token);
        }
        localStorage.setItem('user', JSON.stringify({
          id: student.id || 0,
          name: student.name,
          email: student.email,
          role: student.role || 'STUDENT',
        }));
        if (student.phone) {
          localStorage.setItem('datx_user_phone', student.phone);
        }
        window.dispatchEvent(new CustomEvent('datx-auth-changed'));
        window.dispatchEvent(new CustomEvent('webinars-updated'));
        window.dispatchEvent(new CustomEvent('courses-updated'));

        setIsSuccess(true);
        setMessage(`أهلاً بك مجدداً يا ${getFirstName(student.name, 'طالب DATX')}! تم تسجيل الدخول بنجاح.`);
        setTimeout(() => {
          onClose();
          setMessage('');
        }, 1000);
      } else {
        setIsSuccess(false);
        setMessage('لم نجد تسجيلاً مسبقاً بهذا البريد الإلكتروني. يمكنك التسجيل فوراً في أي ندوة أو دورة.');
      }
    } catch (err) {
      console.error(err);
      setIsSuccess(false);
      setMessage('تعذر التحقق من البريد الإلكتروني، يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-login-modal-title"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-md bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-3xl p-6 md:p-8 border border-blue-500/30 shadow-2xl text-right dir-rtl"
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="إغلاق النافذة المنبثقة"
            title="إغلاق"
            className="absolute top-5 left-5 p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-all focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <FaTimes aria-hidden="true" className="w-4 h-4" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30 mb-3">
              <FaUserCheck aria-hidden="true" className="w-3 h-3 text-blue-400" />
              التحقق عبر البريد الإلكتروني
            </span>
            <h3 id="student-login-modal-title" className="text-2xl font-bold text-white mb-1.5">دخول الطلاب المسجلين</h3>
            <p className="text-xs text-white/60 font-medium">
              أدخل بريدك الإلكتروني للوصول إلى لوحة تعلّمك وروابط الجلسات المباشرة.
            </p>
          </div>

          {message && (
            <div
              id="student-login-msg"
              role="alert"
              aria-live="polite"
              className={`mb-4 p-3.5 rounded-xl border text-xs font-bold ${
                isSuccess
                  ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300'
                  : 'bg-amber-500/20 border-amber-500/30 text-amber-300'
              }`}
            >
              {message}
            </div>
          )}

          {/* Form - Email Only */}
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div>
              <label htmlFor="student-login-email" className="block text-xs font-bold text-white/70 mb-1.5">
                البريد الإلكتروني المسجل *
              </label>
              <div className="relative">
                <input
                  id="student-login-email"
                  name="studentEmail"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="مثال: ahmed@gmail.com"
                  aria-invalid={!isSuccess && message ? 'true' : 'false'}
                  aria-describedby={message ? 'student-login-msg' : undefined}
                  className="w-full pr-10 pl-4 py-3.5 bg-black/40 border border-blue-500/50 rounded-xl text-white text-sm placeholder-white/30 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 transition-all font-medium"
                />
                <FaEnvelope aria-hidden="true" className="absolute top-4 right-3.5 text-blue-400 text-sm" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold rounded-xl shadow-lg shadow-blue-500/25 transition-all flex items-center justify-center gap-2 mt-6 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <FaSignInAlt aria-hidden="true" className="text-sm" />
              {loading ? 'جاري التحقق من البريد...' : 'دخول بحسابي المسجل'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
